import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ShoppingCart from '../../components/ui/ShoppingCart';
import ContactHub from '../../components/ui/ContactHub';
import AuthGateway from '../../components/ui/AuthGateway';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MetricsCard from './components/MetricsCard';
import BookingCalendar from './components/BookingCalendar';
import RecentBookings from './components/RecentBookings';
import QuickActions from './components/QuickActions';
import RevenueChart from './components/RevenueChart';
import NotificationCenter from './components/NotificationCenter';
import MenuControlPanel from './components/MenuControlPanel';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    // Check for authenticated user
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (parsedUser?.type === 'admin') {
        setUser(parsedUser);
      } else {
        // Redirect non-admin users
        navigate('/customer-portal');
      }
    } else {
      setShowAuthModal(true);
    }

    // Update current time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/homepage');
  };

  const metricsData = [
    {
      title: 'Today\'s Bookings',
      value: '12',
      change: '+8%',
      changeType: 'increase',
      icon: 'Calendar',
      color: 'primary'
    },
    {
      title: 'Daily Revenue',
      value: '$2,847',
      change: '+15%',
      changeType: 'increase',
      icon: 'DollarSign',
      color: 'success'
    },
    {
      title: 'Customer Satisfaction',
      value: '4.8/5',
      change: '+0.2',
      changeType: 'increase',
      icon: 'Star',
      color: 'warning'
    },
    {
      title: 'Staff Utilization',
      value: '87%',
      change: '-3%',
      changeType: 'decrease',
      icon: 'Users',
      color: 'secondary'
    }
  ];

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (showAuthModal && !user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] p-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={32} color="var(--color-primary)" />
              </div>
              <h2 className="text-2xl font-bold text-text-primary mb-2">Admin Access Required</h2>
              <p className="text-text-secondary mb-6">Please sign in with administrator credentials to access the dashboard.</p>
              <Button
                variant="default"
                onClick={() => setShowAuthModal(true)}
                iconName="LogIn"
                iconPosition="left"
              >
                Sign In as Admin
              </Button>
            </div>
          </div>
        </div>
        <AuthGateway 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ShoppingCart />
      <ContactHub />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Dashboard Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Admin Dashboard</h1>
                <p className="text-text-secondary mt-1">
                  Welcome back, {user?.name}! Here's what's happening with Clean360 today.
                </p>
                <div className="flex items-center space-x-4 mt-2 text-sm text-text-secondary">
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={14} />
                    <span>{formatDate(currentTime)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{formatTime(currentTime)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => console.log('Export data clicked')}
                >
                  Export Data
                </Button>
                <Button
                  variant="outline"
                  iconName="Settings"
                  iconPosition="left"
                  onClick={() => console.log('Settings clicked')}
                >
                  Settings
                </Button>
                <Button
                  variant="ghost"
                  iconName="LogOut"
                  iconPosition="left"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metricsData?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                color={metric?.color}
              />
            ))}
          </div>

          {/* Menu Control Panel - New Addition */}
          <div className="mb-8">
            <MenuControlPanel />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Left Column - Calendar and Quick Actions */}
            <div className="lg:col-span-2 space-y-8">
              <BookingCalendar />
              <QuickActions />
            </div>

            {/* Right Column - Notifications */}
            <div className="lg:col-span-1">
              <NotificationCenter />
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="mb-8">
            <RevenueChart />
          </div>

          {/* Recent Bookings */}
          <div className="mb-8">
            <RecentBookings />
          </div>

          {/* Emergency Actions */}
          <div className="bg-error/5 border border-error/20 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} color="var(--color-error)" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">Emergency Response</h3>
                  <p className="text-sm text-text-secondary">
                    24/7 emergency booking system with surge pricing and immediate dispatch
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="Phone"
                  iconPosition="left"
                  onClick={() => window.location.href = 'tel:+1234567890'}
                >
                  Emergency Line
                </Button>
                <Button
                  variant="default"
                  iconName="Zap"
                  iconPosition="left"
                  onClick={() => console.log('Emergency booking clicked')}
                >
                  Create Emergency Booking
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;